import React, { useEffect, useMemo, useState } from "react";
import {
  BillingContext,
  MasterPlan,
  RetailPlan,
  assignHierarchyCameraPlan,
  getBillingContext,
  listMasterPlans,
  listRetailPlans,
} from "../../services/billingHierarchyService";

type Camera = {
  cameraId?: string;
  planName?: string;
  billingMasterPlanId?: string;
  billingRetailPlanId?: string;
  billingMonthlyPrice?: number;
  billingCustomerId?: string;
  enabled?: boolean;
  [key: string]: any;
};

type Props = {
  camera: Camera;
  onUpdateCamera?: (camera: any) => void;
};

type VisiblePlan =
  | { kind: "MASTER"; plan: MasterPlan; price: number; priceLabel: string }
  | { kind: "RETAIL"; plan: RetailPlan; price: number; priceLabel: string };

function money(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function formatBitrate(value: number) {
  const kbps = Number(value || 0);
  if (!Number.isFinite(kbps) || kbps <= 0) return "Not specified";
  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(kbps)} Kbps`;
}

function analyticsLabel(value: string) {
  const normalized = String(value || "NONE").trim().toUpperCase();
  if (normalized === "NONE") return "Not included (recording only)";
  return `${normalized.charAt(0)}${normalized.slice(1).toLowerCase()}`;
}

function planConfiguration(item: VisiblePlan) {
  if (item.kind === "RETAIL") return item.plan.configuration;
  return {
    billingModel: item.plan.billingModel || "FIXED",
    name: item.plan.name,
    retentionDays: item.plan.retentionDays,
    analyticsTier: item.plan.analyticsTier,
    resolution: item.plan.resolution,
    fps: item.plan.fps,
    bitrateKbps: item.plan.bitrateKbps,
    codec: item.plan.codec,
  };
}

export default function BillingPlansTabHierarchy({
  camera,
  onUpdateCamera,
}: Props) {
  const [context, setContext] = useState<BillingContext | null>(null);
  const [plans, setPlans] = useState<VisiblePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    void getBillingContext()
      .then(async (billingContext) => {
        if (!active) return;
        setContext(billingContext);

        if (
          billingContext.role === "OWNER" ||
          billingContext.role === "INTEGRATOR"
        ) {
          const items = await listMasterPlans();
          if (!active) return;
          setPlans(
            items
              .filter((plan) => plan.enabled !== false)
              .map((plan) => ({
                kind: "MASTER" as const,
                plan,
                price:
                  billingContext.role === "OWNER"
                    ? Number(plan.defaultWholesaleMonthlyPrice || 0)
                    : Number(plan.wholesaleMonthlyPrice || 0),
                priceLabel:
                  billingContext.role === "OWNER"
                    ? "Owner price"
                    : "Owner-assigned price",
              })),
          );
          return;
        }

        if (billingContext.role === "ADMIN") {
          const items = await listRetailPlans();
          if (!active) return;
          setPlans(
            items
              .filter((plan) => plan.enabled !== false)
              .map((plan) => ({
                kind: "RETAIL" as const,
                plan,
                price: Number(plan.retailMonthlyPrice || 0),
                priceLabel: "Integrator price",
              })),
          );
          return;
        }

        setPlans([]);
      })
      .catch((loadError) => {
        if (active) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Unable to load billing plans.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [camera.cameraId]);

  const currentId = useMemo(
    () =>
      String(
        camera.billingRetailPlanId ||
          camera.billingMasterPlanId ||
          "",
      ).trim(),
    [camera.billingMasterPlanId, camera.billingRetailPlanId],
  );

  const assign = async (item: VisiblePlan) => {
    if (!camera.cameraId) return;
    const id =
      item.kind === "MASTER" ? item.plan.planId : item.plan.retailPlanId;
    setBusyId(id);
    setError("");
    setMessage("");

    try {
      const result = await assignHierarchyCameraPlan({
        cameraId: camera.cameraId,
        configuration: planConfiguration(item),
        masterPlanId: item.kind === "MASTER" ? item.plan.planId : undefined,
        retailPlanId:
          item.kind === "RETAIL" ? item.plan.retailPlanId : undefined,
        customerId: camera.billingCustomerId,
      });
      onUpdateCamera?.(result.camera);
      setMessage(
        `${item.plan.name} assigned. This camera is now eligible to be enabled.`,
      );
    } catch (assignError) {
      setError(
        assignError instanceof Error
          ? assignError.message
          : "Unable to assign this billing plan.",
      );
    } finally {
      setBusyId("");
    }
  };

  const roleDescription =
    context?.role === "OWNER"
      ? "Owner plans are shown with the original Owner price."
      : context?.role === "INTEGRATOR"
        ? "Only plans assigned to this Integrator by the Owner are shown, with the Owner-set price."
        : context?.role === "ADMIN"
          ? "Only plans assigned to this Admin by the Integrator are shown. Owner pricing is private."
          : "No billing plans are available for this account.";

  return (
    <section aria-label="Billing plans" style={{ display: "grid", gap: 18 }}>
      <header>
        <div
          style={{
            color: "#2563eb",
            fontSize: 12,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          Billing
        </div>
        <h2 style={{ margin: "5px 0 6px", color: "#172033" }}>
          Camera billing plan
        </h2>
        <p style={{ margin: 0, color: "#64748b", maxWidth: 850 }}>
          {roleDescription}
        </p>
      </header>

      <div
        style={{
          padding: 14,
          border: "1px solid #dbe3ef",
          borderRadius: 12,
          background: "#f8fafc",
        }}
      >
        <strong>{camera.planName || "No billing plan assigned"}</strong>
        <div style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>
          {camera.planName
            ? `Current camera price: ${money(
                Number(camera.billingMonthlyPrice || 0),
              )} / month`
            : "This camera cannot be enabled until a billing plan is assigned."}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            borderRadius: 10,
            background: "#fef2f2",
            color: "#b91c1c",
          }}
        >
          {error}
        </div>
      )}
      {message && (
        <div
          role="status"
          style={{
            padding: 12,
            borderRadius: 10,
            background: "#f0fdf4",
            color: "#166534",
          }}
        >
          {message}
        </div>
      )}

      {loading ? (
        <div style={{ color: "#64748b" }}>Loading available plans…</div>
      ) : plans.length === 0 ? (
        <div
          style={{
            padding: 16,
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            color: "#64748b",
          }}
        >
          No billing plans have been assigned to this account yet.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
            gap: 16,
          }}
        >
          {plans.map((item) => {
            const id =
              item.kind === "MASTER"
                ? item.plan.planId
                : item.plan.retailPlanId;
            const current = currentId === id;
            const config = planConfiguration(item);
            const isPayg = config.billingModel === "PAYG";
            const currency =
              item.kind === "RETAIL"
                ? item.plan.currency
                : item.plan.currency || "USD";

            return (
              <article
                key={id}
                style={{
                  display: "grid",
                  gap: 14,
                  padding: 20,
                  border: `1px solid ${current ? "#93c5fd" : "#dbe3ef"}`,
                  borderRadius: 16,
                  background: "#fff",
                }}
              >
                <div>
                  <h3 style={{ margin: 0 }}>{item.plan.name}</h3>
                  <span style={{ color: "#64748b", fontSize: 12 }}>
                    {item.priceLabel}
                  </span>
                </div>

                <div>
                  <strong style={{ fontSize: 28 }}>
                    {isPayg ? "Pay as you go" : money(item.price, currency)}
                  </strong>
                  <span style={{ color: "#64748b", fontSize: 12 }}>
                    {isPayg ? " · usage-based billing" : " / camera / month"}
                  </span>
                </div>

                {isPayg ? (
                  <div style={{ color: "#475569", fontSize: 13 }}>
                    Bills measured usage only. Assigning this plan does not change camera
                    quality, retention, recording, motion, or analytics settings.
                  </div>
                ) : (
                  <dl
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 8,
                      margin: 0,
                      fontSize: 13,
                    }}
                  >
                    <dt>Cloud recording</dt>
                    <dd>{config.retentionDays} days</dd>
                    <dt>Resolution</dt>
                    <dd>
                      {config.resolution?.width}×{config.resolution?.height}
                    </dd>
                    <dt>FPS</dt>
                    <dd>{config.fps}</dd>
                    <dt>Bitrate</dt>
                    <dd>{formatBitrate(config.bitrateKbps)}</dd>
                    <dt>Analytics</dt>
                    <dd>{analyticsLabel(config.analyticsTier)}</dd>
                  </dl>
                )}

                <button
                  type="button"
                  disabled={current || Boolean(busyId)}
                  onClick={() => void assign(item)}
                  style={{
                    minHeight: 40,
                    border: 0,
                    borderRadius: 10,
                    background: current ? "#e2e8f0" : "#2563eb",
                    color: current ? "#475569" : "#fff",
                    fontWeight: 800,
                  }}
                >
                  {current
                    ? "Current plan"
                    : busyId === id
                      ? "Assigning…"
                      : "Assign plan"}
                </button>
              </article>
            );
          })}
        </div>
      )}

      <div
        style={{
          padding: 14,
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          background: "#f8fafc",
          color: "#64748b",
          fontSize: 13,
        }}
      >
        PAYG access follows the same Owner → Integrator → Admin assignment chain.
        It is optional and never changes camera settings.
      </div>
    </section>
  );
}
